import React from 'react'
import { useHistory } from 'react-router'
import { UserAdd, UserTick } from 'iconsax-react'

import BaseModal from '../BaseModal2'
import NameSection from './NameSection'
import BasicInfoSection from './BasicInfoSection'
import CustomerDetailsSection from './CustomerDetailsSection'
import KnowledgeSection from './KnowledgeSection'
import EndControls from '../../../molecules/ChatModals/EndControls'
import LoadingButton from '../../../atoms/LoadingButton'
import ExpansionArrow from '../../../atoms/ExpansionArrow'
import TimezoneSection from './TimezoneSection'
import TagsSection from './TagsSection'

import { Routes } from '@SonarCX/Routes'
import { checkE164, isUUID } from '@SonarCX/utilities'
import { useCustomerContext } from '@SonarCX/providers/CustomerProvider'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import useChannelConflictCheck, {
  TYPES
} from '@SonarCX/hooks/useChannelConflictCheck'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { useMetaContext } from '@SonarCX/providers/MetaProvider'
import { putUserMeta } from '@SonarCX/lib/api'
import useStyles from './styles/AddCustomerModal'

const AddCustomerModal = ({ open, onClose, hideInternal, cb }) => {
  const history = useHistory()

  const {
    handleUploadUser,
    setValues,
    emptyVals,
    changed,
    clearChanged,
    values,
    channelsToProvider
  } = useCustomerContext()
  const { meta, setMeta } = useMetaContext()
  const {
    setAction: setCloseAction,
    setReason: setCloseReason,
    setV2
  } = useAreYouSure()
  const {
    value: phone,
    setValue: setPhone,
    valid: phoneValid,
    checked: phoneChecked,
    loading: phoneLoading,
    error: phoneError
  } = useChannelConflictCheck(TYPES.SMS)
  const {
    value: email,
    setValue: setEmail,
    valid: emailValid,
    checked: emailChecked,
    loading: emailLoading,
    error: emailError
  } = useChannelConflictCheck(TYPES.EMAIL)
  const { down451 } = useBreakpoint()

  const topRef = React.useRef<HTMLDivElement>()
  const [expanded, setExpanded] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const loading = React.useMemo(
    () => phoneLoading || emailLoading || saving,
    [phoneLoading, emailLoading, saving]
  )
  const disabled = React.useMemo(() => {
    if (!values.name) {
      return true
    }

    if (phone && !phoneChecked) {
      return true
    }

    if (email && !emailChecked) {
      return true
    }

    return false
  }, [values.name, phone, phoneChecked, email, emailChecked])

  const classes = useStyles()

  React.useEffect(() => {
    const channels = []

    if (phoneChecked) {
      const number = checkE164(phone)[1]
      channels.push({
        type: TYPES.SMS,
        name: 'Phone',
        value: number,
        dirty: true
      })
    }

    if (emailChecked) {
      channels.push({
        type: TYPES.EMAIL,
        name: 'Email',
        value: email,
        dirty: true
      })
    }

    channelsToProvider(channels)
  }, [phoneChecked, phone, emailChecked, email, channelsToProvider])

  const handleClose = () => {
    const action = () => {
      onClose()
      setValues(emptyVals)
      setEmail('')
      setPhone('')
      setExpanded(false)
      channelsToProvider([])
      clearChanged()
    }

    if (changed) {
      setV2(true)
      setCloseReason('Are you sure you want to close this modal?')
      setCloseAction(action)
    } else {
      action()
    }
  }

  const scrollToTop = React.useCallback(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView(false)
    }
  }, [])

  const handleUpload = React.useCallback(
    (close = true) =>
      async () => {
        try {
          setSaving(true)
          const newUser = await handleUploadUser()
          if (!newUser) {
            return
          }

          // Only save values with UUID keys and convert empty strings to null
          const saveableMeta = meta
            .filter(obj => isUUID(obj.key))
            .map(({ key, value, row, row_key }) => {
              let val

              if (value instanceof Date) {
                val = value.toISOString()
              } else {
                val = value.toString().trim()
                if (val.length === 0) {
                  val = null
                }
              }

              if (row_key) {
                return { key, value: val, row, row_key }
              }
              return { key, value: val }
            })

          // Save meta
          await putUserMeta(newUser.id, saveableMeta)
          setValues(emptyVals)
          setEmail('')
          setPhone('')
          channelsToProvider([])
          setMeta([])
          clearChanged()

          if (close) {
            onClose()
            history.push(`${Routes.CUSTOMERS}/${newUser.id}`)
          } else {
            cb && cb()
            scrollToTop()
          }
        } catch (err) {
          console.error('Error saving user meta', err)
        } finally {
          setSaving(false)
        }
      },
    [
      handleUploadUser,
      setValues,
      emptyVals,
      setEmail,
      setPhone,
      clearChanged,
      onClose,
      history,
      channelsToProvider,
      scrollToTop,
      cb,
      meta,
      setMeta
    ]
  )

  return (
    <>
      {/* @ts-ignore */}
      <BaseModal
        open={open}
        onClose={handleClose}
        title='NEW CONTACT'
        PaperProps={{
          style: {
            width: '65%',
            maxHeight: '90%'
          }
        }}
        disableEnforceFocus
        noOverflow
        className={down451 && classes.mobile}
      >
        <div ref={topRef} />

        <NameSection />

        <BasicInfoSection
          phone={phone}
          setPhone={setPhone}
          phoneError={phoneError}
          phoneValid={phoneValid}
          email={email}
          setEmail={setEmail}
          emailError={emailError}
          emailValid={emailValid}
          hideInternal={hideInternal}
        />

        <div className={classes.arrow} onClick={() => setExpanded(!expanded)}>
          <ExpansionArrow expanded={expanded} />{' '}
          {expanded ? 'SHOW LESS' : 'SHOW MORE'}
        </div>

        {expanded && (
          <>
            <TimezoneSection />

            <CustomerDetailsSection create modal canEdit external />

            <TagsSection canEdit modal />

            <KnowledgeSection modal canEdit />
          </>
        )}

        {/* @ts-ignore */}
        <EndControls
          onSubmit={handleUpload()}
          submitText='ADD'
          showExtraIcons={false}
          iconLeft
          SubmitIcon={() => null}
          SubmitButton={props => (
            // @ts-ignore
            <LoadingButton
              loading={loading}
              // @ts-ignore
              Icon={UserTick}
              smallMedia={down451}
              {...props}
            />
          )}
          disabled={disabled}
          saving={saving}
        >
          {/* @ts-ignore */}
          <LoadingButton
            loading={loading}
            onClick={handleUpload(false)}
            disabled={disabled}
            className={classes.button}
            // @ts-ignore
            Icon={UserAdd}
            smallMedia={down451}
          >
            ADD &amp; NEW
          </LoadingButton>
        </EndControls>
      </BaseModal>
    </>
  )
}

export default AddCustomerModal
